import { useState, useCallback } from 'react';
import { formatJSON } from '../utils';
import { getMemberInfo } from '../utils/storage/store';
import { getLanguageStore } from '../utils/storage/language';
import { getCouponListModel, getProductDetailModel } from '../model/store';
import { culTaxAndServiceFee } from '../utils/price';
import math from '../utils/math';

// 优惠券相关逻辑
const useCouponLogic = (storeId, products, priceObj, storeInfo) => {
    const [couponList, setCouponList] = useState([]);
    const [couponInfo, setCouponInfo] = useState(null);
    const [giftProduct, setGiftProduct] = useState(null);

    const getRelateProductIds = useCallback((relateData, type) => {
        const relateDataObj = formatJSON(relateData);
        const obj = relateDataObj?.find(obj => obj.type === type);
        return obj?.id ? obj.id.split(',') : [];
    }, []);

    const isCouponDisabled = useCallback((item) => {
        if (item.status === 1) return true;
        if (item.couponReach && item.couponReach > item.productAmount) return true;
        if (item.couponReachNum && item.couponReachNum > item.totalNum) return true;
        return false;
    }, []);

    const getDiscountAmount = useCallback((productAmount, couponDiscount) => {
        let money = math.sub(productAmount, math.mul(productAmount, couponDiscount));
        money = Math.round(math.mul(money, 100));
        return math.div(money, 100);
    }, []);

    const addGiftProduct = useCallback((coupon) => {
        if (coupon?.couponType === 3 && coupon.giveRelateData) {
            getProductDetailModel({
                productId: coupon.giveRelateData,
                curLanguage: getLanguageStore(),
            }).then(res => {
                setGiftProduct(res);
            }).catch(console.error);
        } else {
            setGiftProduct(null);
        }
    }, []);

    const handleUseCoupon = useCallback(({ curCoupon, store = storeInfo, fromPriceObj = priceObj }) => {
        if (!curCoupon || Object.keys(curCoupon).length === 0) {
            setGiftProduct(null);
            setCouponInfo(null);
            return;
        }
        let reduceAmount = 0;
        let sumTmp = fromPriceObj.pd_actual_price;
        if (curCoupon.couponType === 1) {
            reduceAmount = curCoupon.couponReachDiscount;
        } else if (curCoupon.couponType === 2) {
            reduceAmount = getDiscountAmount(curCoupon.productAmount, curCoupon.couponDiscount);
        }
        sumTmp = math.sub(sumTmp, reduceAmount);
        const oAmount = math.add(sumTmp, reduceAmount);
        const money = store.rateType === 1 ? oAmount : sumTmp;
        const amountItem = culTaxAndServiceFee(money, store, oAmount);
        let totalPrice = amountItem.totalPrice;
        if (store.rateType === 1) {
            totalPrice = math.sub(totalPrice, reduceAmount);
        }
        const priceItem = {
            pd_actual_price: amountItem.pd_actual_price,
            pd_tax: amountItem.pd_tax,
            pd_service: amountItem.pd_service,
            pd_tax_cls: amountItem.pd_tax_cls,
            pd_service_cls: amountItem.pd_service_cls,
            totalPrice
        };
        setCouponInfo({
            info: curCoupon,
            reduceAmount,
            sumTmp,
            priceItem
        });
        addGiftProduct(curCoupon);
    }, [storeInfo, priceObj, getDiscountAmount, addGiftProduct]);

    const getCouponList = useCallback((store, priceObj) => {
        if (!storeId) return;
        const { memberId = '' } = getMemberInfo() || {};
        if (!memberId) return;
        getCouponListModel({
            storeId,
            memberId,
            language: getLanguageStore()
        }, true).then(data => {
            const processedData = data.filter(item => item.fromType !== 4).map(item => {
                if (!item.relateData) item.relateData = '';
                const matchIds = getRelateProductIds(item.relateData, 1);
                const noMatchIds = getRelateProductIds(item.relateData, 2);
                let totalNum = 0;
                const amount = products.reduce((memo, pd) => {
                    let taxFeeObj = null;
                    if (pd.productType) {
                        if (pd.productType.serviceFeeType || pd.productType.taxType) {
                            taxFeeObj = getProductTaxAndServiceFee(pd, pd.amount);
                        } else {
                            taxFeeObj = culTaxAndServiceFee(pd.amount, storeInfo, pd.amount);
                        }
                    }
                    pd.taxFeeObj = taxFeeObj;
                    const totalPrice = taxFeeObj ? taxFeeObj.totalPrice : pd.amount;
                    const productId = pd.productId.toString();
                    if (matchIds.length === 0 && noMatchIds.length === 0) {
                        totalNum = math.add(totalNum, pd.quantity || 1);
                        return math.add(memo, pd.amount);
                    } else if ((matchIds.length > 0 && matchIds.includes(productId)) ||
                        (matchIds.length === 0 && !noMatchIds.includes(productId))) {
                        totalNum = math.add(totalNum, pd.quantity || 1);
                        return math.add(memo, pd.amount);
                    }
                    return memo;
                }, 0);
                item.totalNum = totalNum;
                item.productAmount = amount;
                // 处理使用时间
                let useTimeStr = '';
                if (item.enableTime?.length > 0) {
                    const timeObj = formatJSON(item.enableTime);
                    if (timeObj.hasOwnProperty('all')) {
                        const range = timeObj['all'];
                        if (range) {
                            useTimeStr = `${range.startTime}~${range.endTime}`;
                        }
                    } else {
                        useTimeStr = Object.keys(timeObj).map(k => {
                            const obj = timeObj[k];
                            return `${k}(${obj.startTime}~${obj.endTime})`;
                        }).join();
                    }
                }
                item.useTimeStr = useTimeStr;
                const obj = item.relateData ? formatJSON(item.relateData).find(obj => obj.type === 1) : null;
                item.matchProduct = obj ? obj.id.split(',') : [];
                return item;
            });
            setCouponList(processedData);
        }).catch(console.error);
    }, [storeId, products, getRelateProductIds, isCouponDisabled, storeInfo]);

    // 更新优惠券金额（当商品列表变化时）
    const updateCouponAmount = useCallback((nProducts, callback) => {
        setCouponList(prevList => {
            if (prevList.length === 0) return prevList;

            const processedData = prevList.map(item => {
                const matchIds = getRelateProductIds(item.relateData, 1);
                const noMatchIds = getRelateProductIds(item.relateData, 2);
                let totalNum = 0;

                const amount = nProducts.reduce((memo, pd) => {
                    let taxFeeObj = null;
                    if (pd.productType) {
                        if (pd.productType.serviceFeeType || pd.productType.taxType) {
                            taxFeeObj = getProductTaxAndServiceFee(pd, pd.amount);
                        } else {
                            taxFeeObj = culTaxAndServiceFee(pd.amount, storeInfo, pd.amount);
                        }
                    }
                    pd.taxFeeObj = taxFeeObj;
                    const totalPrice = taxFeeObj ? taxFeeObj.totalPrice : pd.amount;
                    const productId = pd.productId.toString();
                    if (matchIds.length === 0 && noMatchIds.length === 0) {
                        totalNum = math.add(totalNum, pd.quantity || 1);
                        return math.add(memo, pd.amount);
                    } else if ((matchIds.length > 0 && matchIds.includes(productId)) ||
                        (matchIds.length === 0 && !noMatchIds.includes(productId))) {
                        totalNum = math.add(totalNum, pd.quantity || 1);
                        return math.add(memo, pd.amount);
                    }
                    return memo;
                }, 0);

                return {
                    ...item,
                    totalNum,
                    productAmount: amount
                };
            });
            callback && callback(processedData);
            return processedData;
        });
    }, [getRelateProductIds]);


    const getProductTaxAndServiceFee = useCallback((product, totalPrice) => {
        const { productType } = product
        const obj = culTaxAndServiceFee(totalPrice, {
            serviceFeeType: productType.serviceFeeType,
            serviceFeeRate: productType.serviceRate,
            taxType: productType.taxType,
            taxRate: productType.taxRate
        }, totalPrice)
        console.log(obj)
        return obj
    }, []);

    return {
        couponList,
        couponInfo,
        giftProduct,
        handleUseCoupon,
        getCouponList,
        setCouponInfo,
        updateCouponAmount
    };
};

export default useCouponLogic; 